/**
 * DataManagement.jsx
 * Admin interface for managing test data, trash/recycle bin, and data protection
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { 
  Trash2, 
  RotateCcw, 
  AlertTriangle, 
  Shield, 
  Database,
  FileText,
  ShoppingCart,
  Users,
  Activity,
  Loader2,
  CheckCircle,
  XCircle,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import GardePharmacyEditor from './GardePharmacyEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const DataManagement = ({ language = 'fr' }) => {
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    diagnoses: { total: 0, test: 0, deleted: 0 },
    comprehensive_diagnoses: { total: 0, test: 0, deleted: 0 },
    sales: { total: 0, test: 0, deleted: 0 },
    clients: { total: 0, test: 0, deleted: 0 }
  });
  const [trashedItems, setTrashedItems] = useState({
    sales: [],
    diagnoses: [],
    comprehensive_diagnoses: [],
    health_measurements: []
  });
  const [confirmPhrase, setConfirmPhrase] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteType, setDeleteType] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  const CONFIRM_PHRASE = 'SUPPRIMER DONNEES TEST';

  const getToken = () => localStorage.getItem('dermascan_token');

  const fetchStats = React.useCallback(async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API_URL}/admin/data-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  const fetchTrashedItems = React.useCallback(async () => {
    try {
      const token = getToken();
      
      // Fetch trashed sales
      const salesRes = await axios.get(`${API_URL}/sales/admin/trash`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Fetch trashed diagnoses
      const diagRes = await axios.get(`${API_URL}/diagnosis/admin/trash`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Fetch trashed comprehensive diagnoses
      const compDiagRes = await axios.get(`${API_URL}/comprehensive/diagnosis/admin/trash`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Fetch trashed SADAHealth measurements
      let healthRes = { data: [] };
      try {
        healthRes = await axios.get(`${API_URL}/health/admin/measurements/trash`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (e) { /* endpoint may not exist on older deploys */ }
      
      setTrashedItems({
        sales: salesRes.data || [],
        diagnoses: diagRes.data || [],
        comprehensive_diagnoses: compDiagRes.data || [],
        health_measurements: healthRes.data || []
      });
    } catch (error) {
      console.error('Error fetching trashed items:', error);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchTrashedItems();
  }, [fetchStats, fetchTrashedItems]);

  const handleDeleteTestData = async () => {
    if (confirmPhrase !== CONFIRM_PHRASE) {
      toast.error('Phrase de confirmation incorrecte');
      return;
    }

    setLoading(true);
    try {
      const token = getToken();
      const endpoint = deleteType === 'sadaskincare' 
        ? `${API_URL}/admin/reset-sadaskincare`
        : `${API_URL}/admin/clear-test-data`;
      
      const response = await axios.delete(endpoint, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        data: {
          confirm_phrase: CONFIRM_PHRASE,
          reason: deleteReason || 'Suppression via interface admin'
        }
      });
      
      toast.success(response.data.message);
      setShowDeleteDialog(false);
      setConfirmPhrase('');
      setDeleteReason('');
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreItem = async (type, itemId) => {
    setLoading(true);
    try {
      const token = getToken();
      let endpoint = '';
      
      switch (type) {
        case 'sales':
          endpoint = `${API_URL}/sales/admin/${itemId}/restore`;
          break;
        case 'diagnoses':
          endpoint = `${API_URL}/diagnosis/admin/${itemId}/restore`;
          break;
        case 'comprehensive_diagnoses':
          endpoint = `${API_URL}/comprehensive/diagnosis/admin/${itemId}/restore`;
          break;
        case 'health_measurements':
          endpoint = `${API_URL}/health/admin/measurements/${itemId}/restore`;
          break;
        default:
          return;
      }
      
      await axios.post(endpoint, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Élément restauré avec succès');
      fetchTrashedItems();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la restauration');
    } finally {
      setLoading(false);
    }
  };

  const handlePermanentDelete = async (type, itemId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer définitivement cet élément ? Cette action est IRRÉVERSIBLE.')) {
      return;
    }
    
    setLoading(true);
    try {
      const token = getToken();
      let endpoint = '';
      
      switch (type) {
        case 'sales':
          endpoint = `${API_URL}/sales/admin/${itemId}/permanent`;
          break;
        case 'diagnoses':
          endpoint = `${API_URL}/diagnosis/admin/${itemId}/permanent`;
          break;
        case 'comprehensive_diagnoses':
          endpoint = `${API_URL}/comprehensive/diagnosis/admin/${itemId}/permanent`;
          break;
        case 'health_measurements':
          endpoint = `${API_URL}/health/admin/measurements/${itemId}/permanent`;
          break;
        default:
          return;
      }
      
      await axios.delete(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Élément supprimé définitivement');
      fetchTrashedItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setLoading(false);
    }
  };

  const totalTrashed = trashedItems.sales.length + trashedItems.diagnoses.length + trashedItems.comprehensive_diagnoses.length + (trashedItems.health_measurements?.length || 0);

  const StatCard = ({ title, icon: Icon, total, test, deleted, color }) => (
    <Card className="relative overflow-hidden">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">{title}</p>
            <p className="text-2xl font-bold">{total}</p>
          </div>
          <div className={`w-12 h-12 rounded-full ${color} flex items-center justify-center`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
        <div className="mt-3 flex gap-2">
          {test > 0 && (
            <Badge variant="outline" className="text-xs bg-yellow-50 text-yellow-700 border-yellow-200">
              {test} test
            </Badge>
          )}
          {deleted > 0 && (
            <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">
              {deleted} corbeille
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );

  const TrashSection = ({ title, items, type, icon: Icon }) => (
    <div className="space-y-3">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-5 h-5 text-gray-500" />
        <h3 className="font-medium">{title}</h3>
        <Badge variant="secondary">{items.length}</Badge>
      </div>
      
      {items.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-4">Aucun élément dans la corbeille</p>
      ) : (
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {items.map((item, idx) => (
            <div 
              key={item.id || idx} 
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">
                  {type === 'sales' && `Ticket: ${item.ticket_mediciel || 'N/A'} - ${item.client_name || 'Client inconnu'}`}
                  {type === 'diagnoses' && `${item.client_name || 'Client'} - ${item.skin_type || 'Diagnostic'}`}
                  {type === 'comprehensive_diagnoses' && `${item.client_name || 'Client'} - Diagnostic complet`}
                  {type === 'health_measurements' && `${item.patient_name || 'Patient'} — ${item.measured_at ? new Date(item.measured_at).toLocaleDateString('fr-FR') : ''} ${item.systolic ? `| TA ${item.systolic}/${item.diastolic}` : ''}${item.blood_sugar ? ` | Glyc ${item.blood_sugar}` : ''}${item.weight_kg ? ` | ${item.weight_kg}kg` : ''}`}
                </p>
                <p className="text-xs text-gray-500">
                  Supprimé le {new Date(item.deleted_at).toLocaleDateString('fr-FR')} par {item.deleted_by_name || 'Admin'}
                  {item.deletion_reason ? ` · Raison : ${item.deletion_reason}` : ''}
                </p>
              </div>
              <div className="flex gap-2 ml-2">
                <Button 
                  size="sm" 
                  variant="outline"
                  className="text-green-600 hover:bg-green-50"
                  onClick={() => handleRestoreItem(type, item.id)}
                  disabled={loading}
                >
                  <RotateCcw className="w-4 h-4" />
                </Button>
                <Button 
                  size="sm" 
                  variant="outline"
                  className="text-red-600 hover:bg-red-50"
                  onClick={() => handlePermanentDelete(type, item.id)}
                  disabled={loading}
                >
                  <XCircle className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Gestion des données</h2>
          <p className="text-sm text-gray-500">Protection et nettoyage des données</p>
        </div>
        <Button 
          variant="outline" 
          onClick={() => { fetchStats(); fetchTrashedItems(); }}
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <Database className="w-4 h-4" />
            Vue d'ensemble
          </TabsTrigger>
          <TabsTrigger value="trash" className="flex items-center gap-2">
            <Trash2 className="w-4 h-4" />
            Corbeille
            {totalTrashed > 0 && (
              <Badge variant="destructive" className="ml-1">{totalTrashed}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="cleanup" className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Nettoyage
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4 mt-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard 
              title="Ventes" 
              icon={ShoppingCart} 
              total={stats.sales?.total || 0}
              test={stats.sales?.test || 0}
              deleted={stats.sales?.deleted || 0}
              color="bg-blue-500"
            />
            <StatCard 
              title="Diagnostics" 
              icon={FileText} 
              total={stats.diagnoses?.total || 0}
              test={stats.diagnoses?.test || 0}
              deleted={stats.diagnoses?.deleted || 0}
              color="bg-green-500"
            />
            <StatCard 
              title="Diag. Complets" 
              icon={Activity} 
              total={stats.comprehensive_diagnoses?.total || 0}
              test={stats.comprehensive_diagnoses?.test || 0}
              deleted={stats.comprehensive_diagnoses?.deleted || 0}
              color="bg-purple-500"
            />
            <StatCard 
              title="Clients" 
              icon={Users} 
              total={stats.clients?.total || 0}
              test={stats.clients?.test || 0}
              deleted={stats.clients?.deleted || 0}
              color="bg-orange-500"
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-green-600" />
                Protection des données
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <div>
                  <p className="font-medium text-green-800">Soft-delete activé</p>
                  <p className="text-sm text-green-600">Les suppressions vont dans la corbeille (récupérables)</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                <Shield className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="font-medium text-blue-800">Confirmation obligatoire</p>
                  <p className="text-sm text-blue-600">Phrase de sécurité requise pour supprimer les données de test</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-yellow-50 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-yellow-600" />
                <div>
                  <p className="font-medium text-yellow-800">Données de test isolées</p>
                  <p className="text-sm text-yellow-600">Seules les données avec is_test=true peuvent être supprimées en masse</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick garde pharmacy correction */}
          <GardePharmacyEditor />
        </TabsContent>

        {/* Trash Tab */}
        <TabsContent value="trash" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trash2 className="w-5 h-5 text-red-500" />
                Corbeille ({totalTrashed} éléments)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <TrashSection 
                title="Ventes supprimées" 
                items={trashedItems.sales} 
                type="sales"
                icon={ShoppingCart}
              />
              <hr />
              <TrashSection 
                title="Diagnostics simples supprimés" 
                items={trashedItems.diagnoses} 
                type="diagnoses"
                icon={FileText}
              />
              <hr />
              <TrashSection 
                title="Diagnostics complets supprimés" 
                items={trashedItems.comprehensive_diagnoses} 
                type="comprehensive_diagnoses"
                icon={Activity}
              />
              <hr />
              <TrashSection 
                title="Mesures SADAHealth supprimées" 
                items={trashedItems.health_measurements || []} 
                type="health_measurements"
                icon={Activity}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Cleanup Tab */}
        <TabsContent value="cleanup" className="space-y-4 mt-4">
          <Card className="border-yellow-200 bg-yellow-50/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-yellow-800">
                <AlertTriangle className="w-5 h-5" />
                Zone de nettoyage
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-white rounded-lg border border-yellow-200">
                <h4 className="font-medium mb-2">Supprimer les données de TEST uniquement</h4>
                <p className="text-sm text-gray-600 mb-4">
                  Cette action supprime uniquement les documents marqués avec <code className="bg-gray-100 px-1 rounded">is_test: true</code>.
                  Vos vraies données sont protégées.
                </p>
                <div className="flex gap-2">
                  <Button 
                    variant="outline"
                    className="border-yellow-500 text-yellow-700 hover:bg-yellow-100"
                    onClick={() => { setDeleteType('test'); setShowDeleteDialog(true); }}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Supprimer données de test
                  </Button>
                  <Button 
                    variant="outline"
                    className="border-orange-500 text-orange-700 hover:bg-orange-100"
                    onClick={() => { setDeleteType('sadaskincare'); setShowDeleteDialog(true); }}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Reset SADASkinCare (test)
                  </Button>
                </div>
              </div>

              <div className="p-4 bg-gray-100 rounded-lg">
                <h4 className="font-medium mb-2 text-gray-700">Comment marquer des données comme test ?</h4>
                <p className="text-sm text-gray-600">
                  Lors de la création de données de test via l'API, ajoutez le champ :
                </p>
                <pre className="mt-2 p-2 bg-gray-800 text-green-400 rounded text-xs overflow-x-auto">
{`{
  "client_name": "Test Client",
  "is_test": true,  // <-- Ajouter ce champ
  ...
}`}
                </pre>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Confirmation de suppression
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <p>
                Vous êtes sur le point de supprimer les données de test 
                ({deleteType === 'sadaskincare' ? 'SADASkinCare' : 'toutes collections'}).
              </p>
              <p className="font-medium text-gray-900">
                Seules les données avec <code className="bg-gray-100 px-1 rounded">is_test: true</code> seront supprimées.
              </p>
              
              <div className="space-y-3 pt-2">
                <div>
                  <Label htmlFor="reason">Raison (optionnel)</Label>
                  <Input 
                    id="reason"
                    value={deleteReason}
                    onChange={(e) => setDeleteReason(e.target.value)}
                    placeholder="Ex: Nettoyage après tests"
                  />
                </div>
                
                <div>
                  <Label htmlFor="confirm" className="text-red-600">
                    Tapez exactement : <strong>SUPPRIMER DONNEES TEST</strong>
                  </Label>
                  <Input 
                    id="confirm"
                    value={confirmPhrase}
                    onChange={(e) => setConfirmPhrase(e.target.value)}
                    placeholder="SUPPRIMER DONNEES TEST"
                    className="mt-1 border-red-200 focus:border-red-500"
                  />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setConfirmPhrase(''); setDeleteReason(''); }}>
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTestData}
              disabled={confirmPhrase !== CONFIRM_PHRASE || loading}
              className="bg-red-600 hover:bg-red-700"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Confirmer la suppression
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DataManagement;
